import {
  ColumnHeightOutlined,
  DeleteOutlined,
  PlusOutlined,
  VerticalAlignMiddleOutlined,
} from "@ant-design/icons";
import { Button, Empty } from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { ZAttrMeta } from "src/business/attribute";
import { CollapseButton } from "src/components/CollapseButton";
import { DeleteConfirm } from "src/components/DeleteConfirm";

import { RenderCtx } from "../../node";
import { MfNodeAttrList } from "../../node/MfNodeAttrList";
import * as styles from "./MfAttrListCollapsible.module.less";

type PropsMfAttrListCollapsible = {
  attrMeta: ZAttrMeta;
  node: MfNodeAttrList;
  ctx: RenderCtx;
};

type DeleteInfo = {
  index: number;
  key: string;
};

export const MfAttrListCollapsible: React.FC<PropsMfAttrListCollapsible> =
  observer((props) => {
    const { node, attrMeta, ctx } = props;
    const totalCount = node.items?.length ?? 0;
    const canCollapseAll = totalCount > 0 && !node.collapsed;
    const [toDelete, setToDelete] = React.useState<DeleteInfo | undefined>();
    let addReason = "";
    if (node.disabled) {
      addReason = "Запрещено для редактирования";
    } else {
      const { type } = attrMeta;
      if (type.name === "list") {
        const { maxCount } = type;
        if (maxCount && totalCount >= maxCount)
          addReason = `Максимальное допустимое число строк = ${maxCount}`;
      }
    }
    return (
      <div className={styles.mainBox}>
        <div className={styles.mainHeader}>
          <CollapseButton
            collapsed={node.collapsed}
            onChange={(value) => node.setCollapsed(value)}
          />
          <div className={styles.mainTitle}>
            {attrMeta.edit?.title ?? attrMeta.title}: {totalCount}
          </div>
          {canCollapseAll && (
            <Button
              size="small"
              icon={<VerticalAlignMiddleOutlined />}
              title="Свернуть все строки"
              onClick={() => node.setCollapsedRecord("all", true)}
            />
          )}
          {canCollapseAll && (
            <Button
              size="small"
              icon={<ColumnHeightOutlined />}
              title="Развернуть все строки"
              onClick={() => node.setCollapsedRecord("all", false)}
            />
          )}
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            title={`${addReason || "Добавить новую строку"}`}
            disabled={!!addReason}
            onClick={() => {
              node.add();
              ctx.onChange();
            }}
          />
        </div>
        {!node.collapsed && (
          <div className={styles.mainContent}>
            {totalCount === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            {totalCount > 0 &&
              Object.entries(node.recordNodes).map(
                ([recKey, { Render }], i) => (
                  <div key={recKey} className={styles.recordBox}>
                    <div className={styles.recordHeader}>
                      <CollapseButton
                        collapsed={node.collapsedRecords.has(recKey)}
                        onChange={(v) => {
                          node.setCollapsedRecord(recKey, v);
                        }}
                      />
                      <div className={styles.recordTitle}>{`${i + 1}`}</div>
                      <Button
                        size="small"
                        icon={<DeleteOutlined />}
                        title={`Удалить строку № ${i + 1} со всеми вложенными атрибутами`}
                        disabled={node.disabled}
                        onClick={() => setToDelete({ index: i, key: recKey })}
                      />
                    </div>
                    {!node.collapsedRecords.has(recKey) && (
                      <div className={styles.recordContent}>
                        <Render ctx={ctx} />
                      </div>
                    )}
                  </div>
                ),
              )}
          </div>
        )}
        <DeleteConfirm
          content={toDelete ? `Удалить строку № ${toDelete.index + 1} ?` : ""}
          open={!!toDelete}
          task={async () => {
            if (!toDelete) throw Error("Не выбрано");
            node.deleteRecord(toDelete.key);
            ctx.onChange();
          }}
          close={() => setToDelete(undefined)}
        />
      </div>
    );
  });
